import type { ReactNode } from "react";
import {
  OverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  type TooltipProps as AriaTooltipProps,
} from "react-aria-components";
import { cn } from "@core/utils";

interface TooltipProps extends Omit<
  AriaTooltipProps,
  "className" | "children"
> {
  /** The tooltip's own content. Plain text — a tooltip is not a place for controls. */
  content: ReactNode;
  /** The element the tooltip describes. Must accept a ref and forward DOM props. */
  children: ReactNode;
  /** Milliseconds before it opens on hover. Zero for tooltips that replace clipped text. */
  delay?: number;
  isDisabled?: boolean;
  className?: string;
}

/**
 * Tooltip primitive on React Aria's `TooltipTrigger`.
 *
 * ⚠ HOVER **AND** FOCUS, which is the reason to take the React Aria one rather than a CSS
 * `:hover` trick. A tooltip that only appears on hover is invisible to a keyboard user and to a
 * screen reader, and the text it is standing in for is usually text that has been CLIPPED — so the
 * information is not available anywhere else on the screen. React Aria wires `aria-describedby`,
 * opens on focus, closes on Escape, and honours the pointer/keyboard distinction.
 *
 * ⚠ NOT FOR ANYTHING INTERACTIVE. A tooltip is unreachable on touch and dismissed by moving the
 * pointer, so nothing may live in one that a person has to click, copy, or read at their own pace.
 * It supplements; it never carries the only copy of something actionable.
 */
export function Tooltip({
  content,
  children,
  delay = 300,
  isDisabled = false,
  className,
  ...props
}: TooltipProps) {
  if (isDisabled) return <>{children}</>;

  return (
    <AriaTooltipTrigger delay={delay} closeDelay={100}>
      {children}
      <AriaTooltip
        {...props}
        offset={6}
        className={cn(
          "max-w-80 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs-medium text-white shadow-lg",
          // `break-words` because the content is usually the long string that was clipped in the
          // first place — a tooltip that overflows the viewport helps nobody.
          "break-words",
          "entering:animate-in entering:fade-in entering:zoom-in-95",
          "exiting:animate-out exiting:fade-out exiting:zoom-out-95",
          className
        )}
      >
        <OverlayArrow>
          <svg
            width={8}
            height={8}
            viewBox="0 0 8 8"
            aria-hidden
            className="fill-gray-900 group-placement-bottom:rotate-180 group-placement-left:-rotate-90 group-placement-right:rotate-90"
          >
            <path d="M0 0 L4 4 L8 0 Z" />
          </svg>
        </OverlayArrow>
        {content}
      </AriaTooltip>
    </AriaTooltipTrigger>
  );
}
